import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { TAB_PATHS, tabFromPath, type Tab } from './tabRoutes';

export type { Tab };
export { TAB_PATHS, tabFromPath };

interface TabContextValue {
  activeTab: Tab;
  setActiveTab: (tab: Tab) => void;
}

const TabContext = createContext<TabContextValue | undefined>(undefined);

export function TabProvider({ children }: { children: ReactNode }) {
  // The URL is the source of truth, so a link to /report opens on the report.
  const [activeTab, setTab] = useState<Tab>(() =>
    typeof window === 'undefined' ? 'current' : tabFromPath(window.location.pathname),
  );

  useEffect(() => {
    const onPopState = () => setTab(tabFromPath(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const setActiveTab = useCallback((tab: Tab) => {
    setTab(tab);
    // pushState rather than replaceState so the back button steps between tabs
    // the way a reader expects.
    if (window.location.pathname !== TAB_PATHS[tab]) {
      window.history.pushState(null, '', TAB_PATHS[tab]);
    }
  }, []);

  return (
    <TabContext.Provider value={{ activeTab, setActiveTab }}>
      {children}
    </TabContext.Provider>
  );
}

export function useTab() {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTab must be used within TabProvider');
  }
  return context;
}
